// ✅ src/hooks/useSubscription.js
import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

function safeStr(v) {
  return String(v ?? "").trim();
}

function toMillis(v) {
  try {
    if (!v) return null;
    if (typeof v === "number") return v;
    if (v?.toMillis) return v.toMillis();
    if (v?.toDate) return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();

    // fallback: try parse string/other
    const d = new Date(v);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

export default function useSubscription() {
  const [uid, setUid] = useState("");
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [err, setErr] = useState("");

  // track auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || "");
    });
    return () => unsub();
  }, []);

  // listen to appSubscriptions/{uid}
  useEffect(() => {
    setErr("");

    if (!uid) {
      setSub(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const ref = doc(db, "appSubscriptions", uid);

    const unsub = onSnapshot(
      ref,
      async (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setSub(data);
        setLoading(false);

        // ✅ OPTIONAL: if subscription is expired but still marked active, flip to expired
        try {
          if (!data) return;

          const status = safeStr(data.status).toLowerCase();
          const endMs = toMillis(data.currentPeriodEnd);
          const isExpired = endMs ? endMs <= Date.now() : true;

          if (status === "active" && isExpired) {
            await setDoc(
              ref,
              { status: "expired", updatedAt: serverTimestamp() },
              { merge: true }
            );
          }
        } catch {
          // silent: don't break UI if this fails
        }
      },
      (e) => {
        console.error("[useSubscription] read error:", e);
        setErr(String(e?.message || e));
        setSub(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  const plan = useMemo(() => safeStr(sub?.plan) || "Free", [sub]);

  const isPlus = useMemo(() => plan.toLowerCase() === "plus", [plan]);
  const isPro = useMemo(() => plan.toLowerCase() === "pro", [plan]);
  const isVIP = useMemo(() => plan.toLowerCase() === "vip", [plan]);

  const active = useMemo(() => {
    const status = safeStr(sub?.status).toLowerCase(); // active / ACTIVE / expired
    const endMs = toMillis(sub?.currentPeriodEnd);
    const notExpired = endMs ? endMs > Date.now() : false;
    return status === "active" && notExpired;
  }, [sub]);

  const allowed = useMemo(() => {
    // ✅ allow if active AND has a valid plan
    return active && (isPlus || isPro || isVIP);
  }, [active, isPlus, isPro, isVIP]);

  return { loading, uid, plan, isPlus, isPro, isVIP, active, allowed, sub, err };
}